export async function normalizeHeic(file: File): Promise<Blob> {
  const isHeic = /heic|heif/i.test(file.type) || /\.(heic|heif)$/i.test(file.name);
  if (!isHeic) return file;
  const { default: heic2any } = await import('heic2any');
  const result = await heic2any({ blob: file, toType: 'image/jpeg', quality: 0.9 });
  return Array.isArray(result) ? result[0] : result;
}

function createImage(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = reject;
    image.src = url;
  });
}

function rotatedSize(width: number, height: number, rotation: number) {
  const radians = rotation * Math.PI / 180;
  return {
    width: Math.abs(Math.cos(radians) * width) + Math.abs(Math.sin(radians) * height),
    height: Math.abs(Math.sin(radians) * width) + Math.abs(Math.cos(radians) * height),
  };
}

export async function cropAndCompress(
  imageUrl: string,
  crop: { x: number; y: number; width: number; height: number },
  rotation: number,
): Promise<Blob> {
  const image = await createImage(imageUrl);
  const bounds = rotatedSize(image.naturalWidth, image.naturalHeight, rotation);
  const canvas = document.createElement('canvas');
  canvas.width = Math.ceil(bounds.width);
  canvas.height = Math.ceil(bounds.height);
  const context = canvas.getContext('2d', { willReadFrequently: true });
  if (!context) throw new Error('ไม่สามารถประมวลผลภาพได้');
  context.translate(canvas.width / 2, canvas.height / 2);
  context.rotate(rotation * Math.PI / 180);
  context.translate(-image.naturalWidth / 2, -image.naturalHeight / 2);
  context.drawImage(image, 0, 0);
  const output = document.createElement('canvas');
  const scale = Math.min(1, 2400 / Math.max(crop.width, crop.height));
  output.width = Math.max(1, Math.round(crop.width * scale));
  output.height = Math.max(1, Math.round(crop.height * scale));
  const outputContext = output.getContext('2d');
  if (!outputContext) throw new Error('ไม่สามารถประมวลผลภาพได้');
  outputContext.drawImage(canvas, crop.x, crop.y, crop.width, crop.height, 0, 0, output.width, output.height);
  const blob = await new Promise<Blob | null>((resolve) => output.toBlob(resolve, 'image/webp', 0.86));
  if (!blob) throw new Error('ไม่สามารถบีบอัดภาพได้');
  return blob;
}

export async function sha256Hex(blob: Blob): Promise<string> {
  const digest = new Uint8Array(await crypto.subtle.digest('SHA-256', await blob.arrayBuffer()));
  return [...digest].map((byte) => byte.toString(16).padStart(2, '0')).join('');
}
